import React, { memo, useCallback, useMemo } from 'react'
import { GridColumnVisibilityModel } from '@mui/x-data-grid-pro'
import { useSelector } from 'react-redux'
import { EmptyLmsTrainings } from 'src/components/lms-training/grid/EmptyLmsTrainings'
import { GridTable } from 'src/components/shared/grid/GridTable'
import {
  LMS_COURSES_GRID_COLUMNS_STORAGE_KEY,
  lmsTrainingPageSizeOptions,
} from 'src/constants/lms-training/constants'
import { LmsTrainingsTableFields } from 'src/constants/lms-training/enums'
import { buildLmsTrainingColumns } from 'src/constants/lms-training/lmsTrainingGrid'
import type { LmsTrainingPlain } from 'src/interfaces/collectStepReference'
import { getLmsTrainings, getLmsTrainingsLoading } from 'src/selectors/lmsTraining'
import { filterLmsTrainings } from 'src/utils/lmsTrainingList'
import { useGridColumns } from 'src/utils/hooks/grid/useGridColumns'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'

interface Props {
  searchQuery: string
  canManage: boolean
  onEditLmsTraining(lmsTraining: LmsTrainingPlain): void
  onDeleteLmsTraining(lmsTraining: LmsTrainingPlain): void
}

const getRowId = (row: LmsTrainingPlain) => {
  return row.id
}

// Title identifies the row and the actions column has no meaning when hidden; both also carry
// `hideable: false`, which is what removes them from the manage-columns panel. This guard
// additionally stops a programmatic visibility-model change from persisting a hide.
const UNHIDEABLE_COLUMNS: Array<string> = [
  LmsTrainingsTableFields.title,
  LmsTrainingsTableFields.actions,
]

const LmsTrainingsTableComponent = ({
  searchQuery,
  canManage,
  onEditLmsTraining,
  onDeleteLmsTraining,
}: Props) => {
  const lmsTrainings = useSelector(getLmsTrainings)
  const isLoading = useSelector(getLmsTrainingsLoading)

  const { paginationModel, setPaginationModel } = useGridPagination(lmsTrainingPageSizeOptions)

  // A fresh array each render would reset the grid's column state, so the factory call is
  // memoized — it depends only on the two stable handlers and the permission flag, which is fixed
  // for the session, so a delete no longer rebuilds it.
  const lmsTrainingColumns = useMemo(() => {
    return buildLmsTrainingColumns(onEditLmsTraining, onDeleteLmsTraining, canManage)
  }, [onEditLmsTraining, onDeleteLmsTraining, canManage])

  const { includedColumns, handleAddExcludedColumn, handleRemoveExcludedColumn } = useGridColumns(
    lmsTrainingColumns,
    LMS_COURSES_GRID_COLUMNS_STORAGE_KEY,
  )

  // The client holds the whole catalog (the endpoint takes no search param), so filtering is here.
  const visibleLmsTrainings = useMemo(() => {
    return filterLmsTrainings(lmsTrainings, searchQuery)
  }, [lmsTrainings, searchQuery])

  const columnVisibilityModel = useMemo(() => {
    return lmsTrainingColumns.reduce<GridColumnVisibilityModel>((accumulator, column) => {
      accumulator[column.field] = includedColumns.includes(column.field)

      return accumulator
    }, {})
  }, [lmsTrainingColumns, includedColumns])

  // `useGridColumns` stores excluded fields as a flat list; the grid speaks a visibility model.
  // Translate both ways so menu changes persist to localStorage across reloads.
  const handleColumnVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      lmsTrainingColumns.forEach((column) => {
        if (UNHIDEABLE_COLUMNS.includes(column.field)) {
          return
        }

        if (model[column.field] === false) {
          handleAddExcludedColumn(column.field)

          return
        }

        handleRemoveExcludedColumn(column.field)
      })
    },
    [lmsTrainingColumns, handleAddExcludedColumn, handleRemoveExcludedColumn],
  )

  const hasSearchQuery = searchQuery.trim() !== ''

  // `GridTable` renders EmptyElement with no props, so the search-aware copy is bound here.
  const EmptyElement = useMemo(() => {
    const BoundEmptyLmsTrainings = () => {
      return <EmptyLmsTrainings hasSearchQuery={hasSearchQuery} />
    }

    return BoundEmptyLmsTrainings
  }, [hasSearchQuery])

  // Pinning is intentionally left uncontrolled, for the reason documented on CustomersTable:
  // `useGridColumns`'s persisted list has no left/right distinction.
  return (
    <GridTable<LmsTrainingPlain>
      rows={visibleLmsTrainings}
      columns={lmsTrainingColumns}
      getRowId={getRowId}
      isLoading={isLoading}
      sortingMode="client"
      paginationMode="client"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={lmsTrainingPageSizeOptions}
      initialState={{
        // The backend list query has NO `ORDER BY` (para-node LmsTrainingRepository.findAll is
        // a bare getMany), so this sort model is the only thing making the order stable.
        sorting: { sortModel: [{ field: LmsTrainingsTableFields.title, sort: 'asc' }] },
      }}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
      EmptyElement={EmptyElement}
      tableDataTestId="lms-courses-list-table"
      rowDataTestIdPrefix="lms-courses-list"
    />
  )
}

export const LmsTrainingsTable = memo(LmsTrainingsTableComponent)
